import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Region {
  id: number;
  name: string;
  language: string;
  stories: string;
  listeners: string;
  image: string;
  alt: string;
  color: string;
}

interface RegionalHighlightsProps {
  regions: Region[];
}

export default function RegionalHighlights({ regions }: RegionalHighlightsProps) {
  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-accent/5 via-background to-primary/5">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="GlobeAltIcon" size={20} className="text-accent" />
            <span className="text-sm font-semibold text-accent">Regional Content</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-text-primary mb-4">
            Stories from Every Corner of India
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Celebrate India's linguistic diversity with authentic stories in your mother tongue
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {regions.map((region) => (
            <div
              key={region.id}
              className="group relative bg-card rounded-2xl overflow-hidden shadow-warm hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-2"
            >
              <div className="relative aspect-[3/4] overflow-hidden">
                <AppImage
                  src={region.image}
                  alt={region.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
                
                <div className={`absolute top-4 left-4 w-12 h-12 ${region.color} rounded-full flex items-center justify-center shadow-warm`}>
                  <Icon name="MapPinIcon" size={24} variant="solid" className="text-white" />
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 space-y-3">
                  <div>
                    <h3 className="font-heading font-bold text-2xl text-white mb-1">
                      {region.name}
                    </h3>
                    <p className="text-sm text-white/80">{region.language}</p>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-white/20">
                    <div className="flex items-center space-x-1 text-white/90">
                      <Icon name="BookOpenIcon" size={16} />
                      <span className="text-sm">{region.stories}</span>
                    </div>
                    <div className="flex items-center space-x-1 text-white/90">
                      <Icon name="UserGroupIcon" size={16} />
                      <span className="text-sm">{region.listeners}</span>
                    </div>
                  </div>

                  <button className="w-full py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/30 transition-all duration-300 opacity-0 group-hover:opacity-100">
                    Explore Region
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="/regional-hubs"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-accent text-accent-foreground rounded-xl font-cta font-bold text-lg transition-all duration-300 hover:bg-accent/90 hover:shadow-warm-lg hover:scale-105"
          >
            <span>Explore All Regions</span>
            <Icon name="ArrowRightIcon" size={20} />
          </a>
        </div>
      </div>
    </section>
  );
}